import React, { useEffect, useRef } from "react";
import Layout from "../../../components/admin/Layout";
import Helmet from "react-helmet";
import Table from "../../../components/ui/Table";
import usePaginate from "../../../hooks/api/usePaginate";
import apiRequest from "../../../utils/admin/apiRequest";
import useModal from "../../../hooks/ui/useModal";
import Pagination from "../../../components/ui/Pagination";
import { FaEdit, FaPlus, FaTrashAlt } from "react-icons/fa";


import Modal from "../../../components/ui/utils/Modal";
import Button from "../../../components/ui/utils/Button";
import useFormInput from "../../../hooks/form/useFormInput";
import InputField from "../../../components/ui/form/InputField";
import { useSelector } from "react-redux";
import Delete from "../../../components/admin/vendor/paymentGateway/Delete";
import Edit from "../../../components/admin/vendor/paymentGateway/Edit";
import Create from "../../../components/admin/vendor/paymentGateway/Create";

const PaymentGateway = () => {
    const {
        currentPage,
        data,
        error,
        fetchData,
        limit,
        loading,
        setPage,
        setSorting,
        sort,
        totalItems,
        totalPages,
        setFilters,
    } = usePaginate({
        apiMethod: apiRequest.getVendorManagerPaymentGateway,
        dataKey: "gateways",
    });

    const { isOpen, openModal, closeModal, actionType } = useModal();

    const handleSort = (field) => {
        const order =
            sort.sortField === field && sort.sortOrder === "asc" ? "desc" : "asc";
        setSorting({ sortField: field, sortOrder: order });
    };

    const searchInitialValues = { vendorManagerId: "" };
    const { values, handleChange, setValues } =
        useFormInput(searchInitialValues);

    const debounceRef = useRef(null);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);

        debounceRef.current = setTimeout(() => {
            const filters = {};
            if (values.vendorManagerId) {
                filters.vendorManagerId = {
                    $regex: values.vendorManagerId,
                    $options: "i",
                };
            }
            setFilters(filters);
        }, 500);

        return () => clearTimeout(debounceRef.current);
    }, [values.vendorManagerId]);

    const BRAND_NAME = useSelector(
        (state) => state.globalState?.siteSetting?.site_settings?.site_name
    );




    return (
        <Layout>
            <Helmet>
                <title>Payment Gateway - {BRAND_NAME ?? ""}</title>
            </Helmet>

            <div className="p-4 bg-white rounded">
                <div className="flex items-center gap-1 py-2">
                    <Button
                        onClick={() => openModal({ type: "add" })}
                        variant="primary"
                    >
                        <div className="flex items-center gap-3">
                            <FaPlus />
                            <p>Add New</p>
                        </div>
                    </Button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-[1fr_100px] gap-2 my-2">
                    <InputField
                        name="vendorManagerId"
                        placeholder="Search Vendor Manager ID"
                        value={values.vendorManagerId}
                        onChange={handleChange}
                    />
                    <button
                        className="py-1 text-white rounded bg-emerald-600 hover:bg-emerald-800"
                        onClick={() => setValues(searchInitialValues)}
                    >
                        Clear
                    </button>
                </div>

                {/* Table */}
                <Table
                    headers={[
                        { label: "Vendor Manager", key: "vendorManagerId", sorting: true },
                        { label: "Gateway", key: "gatewayName", sorting: true },
                        { label: "Key ID", key: "keyId", sorting: false },
                        { label: "Key Secret", key: "keySecret", sorting: false },
                        { label: "Status", key: "status", sorting: true },
                        { label: "Action", key: "action", sorting: false },
                    ]}
                    loading={loading}
                    handleSort={handleSort}
                    sort={sort}
                    error={error}
                    cols={5}
                    renderRow={
                        <tbody className="bg-white divide-y divide-gray-200">
                            {data?.map((gateway) => (
                                <tr
                                    key={gateway._id}
                                    className="transition-colors hover:bg-gray-50"
                                >
                                    <td className="px-3 py-3 text-xs">
                                        {gateway?.vendorManagerId?.name}
                                    </td>

                                    <td className="px-3 py-3 capitalize">
                                        {gateway.gatewayName}
                                    </td>

                                    <td className="px-3 py-3 text-xs text-gray-500">
                                        {gateway?.razorpay?.keyId || "—"}
                                    </td>
                                    <td className="px-3 py-3 text-xs text-gray-500">
                                        {gateway?.razorpay?.keySecret || "—"}
                                    </td>

                                    <td className="px-3 py-3">
                                        <span
                                            className={`inline-block px-2 py-1 text-xs font-semibold rounded ${gateway.isActive
                                                ? "bg-green-100 text-green-700"
                                                : "bg-yellow-100 text-yellow-700"
                                                }`}
                                        >
                                            {gateway.isActive ? "Active" : "Inactive"}
                                        </span>
                                    </td>

                                    <td className="px-3 py-3">
                                        <div className="flex justify-center gap-3">
                                            <button
                                                onClick={() =>
                                                    openModal({
                                                        type: "edit",
                                                        data: { state: gateway },
                                                    })
                                                }
                                                className="text-primary"
                                                title="Edit"
                                            >
                                                <FaEdit size={18} />
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() =>
                                                    openModal({
                                                        type: "delete",
                                                        data: gateway,
                                                    })
                                                }
                                                className="text-red-500"
                                                title="Delete"
                                            >
                                                <FaTrashAlt size={18} />
                                            </button>

                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    }
                />

                {totalPages > 1 && (
                    <Pagination
                        pagination={{
                            currentPage,
                            totalPages,
                            totalItems,
                            perPage: limit,
                        }}
                        handlePageChange={setPage}
                        loading={loading}
                    />
                )}
            </div>

            <Modal isOpen={isOpen} onClose={closeModal} size="lg">
                {actionType?.type === "add" && (
                    <Create
                        closeModal={closeModal}
                        callback={fetchData}
                    />
                )}

                {actionType?.type === "edit" && (
                    <Edit
                        data={actionType?.data?.state}
                        closeModal={closeModal}
                        callback={fetchData}
                    />
                )}

                {actionType?.type === "delete" && (
                    <Delete
                       data={actionType?.data}  
                        closeModal={closeModal}
                        callback={fetchData}
                    />
                )}
            </Modal>
        </Layout>
    );
};

export default PaymentGateway;
